import type { ReactiveController, ReactiveControllerHost } from 'lit';
import type { Placement, PlacementControllerOptions, Coords } from '../types.js';
import { deepQueryById, computeCoords, clampToViewport } from '../utils/dom.utils.js';

// ─────────────────────────────────────────────────────────────────────────────
// Host interface — what PlacementController reads from its host element
// ─────────────────────────────────────────────────────────────────────────────

export interface PlacementHost extends ReactiveControllerHost {
  placement: Placement;
  offset:    number;
  shadowRoot: ShadowRoot | null;
}

// ─────────────────────────────────────────────────────────────────────────────
// PlacementController
// ─────────────────────────────────────────────────────────────────────────────

/**
 * A Lit Reactive Controller that centralises ALL overlay positioning machinery.
 *
 * Every hard-won fix from the debugging sessions is embedded here and
 * inherited by every component that uses this controller:
 *
 *  FIX 1 — No double-wiring on first render
 *    `firstUpdated` + `_firstRenderDone` guard. Lit calls `updated(changed)`
 *    on the first render with every property in `changed` (including
 *    `triggerId`). Without the guard, both `firstUpdated()` and `updated()`
 *    would call `_wire()`, attaching two click listeners, causing a toggle
 *    open-then-immediately-close race.
 *
 *  FIX 2 — rAF after showPopover()
 *    `showPopover()` promotes the element to the browser top layer, but the
 *    browser hasn't performed layout yet at that microtask tick.
 *    `getBoundingClientRect()` returns {width:0, height:0}. Waiting one rAF
 *    ensures real layout dimensions before measurement.
 *
 *  FIX 3 — No inline style reset on close
 *    The CSS `transition: overlay Xms allow-discrete` keeps the element
 *    promoted in the top layer for the full animation duration while it
 *    fades out. Clearing `style.top/left` during this window causes the UA's
 *    `inset: 0 auto auto 0` to snap the panel to the viewport's top-left
 *    corner and fade out there — visible flicker. Inline styles are left
 *    intact; `position()` always writes fresh values on the next `show()`.
 *
 *  FIX 4 — No !important on positional CSS
 *    CSS cascade: `!important` in ANY stylesheet beats a normal inline style.
 *    `top: 0 !important` in the shadow stylesheet beats `el.style.top = '200px'`.
 *    Without !important, author stylesheet loses to inline style (which has
 *    higher specificity: [1,0,0,0] vs [0,0,1,0]). All positional rules use
 *    plain author declarations.
 *
 *  FIX 5 — setTimeout(0) for outside-click dismiss
 *    `stopPropagation()` on the trigger blocks bubbling only. A
 *    `document.addEventListener(click, …, { capture: true })` fires in the
 *    capture phase — BEFORE the target handler — and catches the opening
 *    click. Even with rAF, the capture listener may be registered within the
 *    same task. A `setTimeout(0)` puts registration in a brand-new task,
 *    guaranteed after the entire event-loop drain of the opening interaction.
 */
export class PlacementController implements ReactiveController {
  // ── Public ───────────────────────────────────────────────────────
  readonly host: PlacementHost;
  triggerEl: Element | null = null;

  get open(): boolean { return this._open; }

  // ── Private ──────────────────────────────────────────────────────
  private readonly _getPanelEl:            () => HTMLElement | null;
  private readonly _onTriggerFound:        (el: Element) => (() => void) | undefined;
  private readonly _dismissOnOutsideClick: boolean;
  private readonly _dismissOnEscape:       boolean;
  private readonly _afterShow:             (() => void) | null;
  private readonly _afterHide:             (() => void) | null;
  private readonly _afterPosition:         ((d: { triggerRect: DOMRect; panelCoords: Coords; panelEl: HTMLElement }) => void) | null;

  private _open                   = false;
  private _firstRenderDone        = false;
  private _triggerId:              string | null = null;
  private _triggerSlot:            HTMLSlotElement | null = null;
  private _cleanupFns:             Array<() => void> = [];
  private _triggerListenerCleanup: (() => void) | null = null;
  private _outsideClickTimer:      ReturnType<typeof setTimeout> | null = null;

  // Pre-bound listener references — required for removeEventListener identity
  private readonly _onOutsideClick: (e: MouseEvent) => void;
  private readonly _onEscapeKey:    (e: KeyboardEvent) => void;
  private readonly _onScroll:       () => void;
  private readonly _onResize:       () => void;

  constructor(host: PlacementHost, options: PlacementControllerOptions) {
    this.host = host;
    host.addController(this);  // register with Lit's reactive controller system

    this._getPanelEl            = options.getPanelEl;
    this._onTriggerFound        = options.onTriggerFound        ?? (() => undefined);
    this._dismissOnOutsideClick = options.dismissOnOutsideClick ?? false;
    this._dismissOnEscape       = options.dismissOnEscape       ?? false;
    this._afterShow             = options.afterShow             ?? null;
    this._afterHide             = options.afterHide             ?? null;
    this._afterPosition         = options.afterPosition         ?? null;

    this._onOutsideClick = this._handleOutsideClick.bind(this);
    this._onEscapeKey    = this._handleEscapeKey.bind(this);
    this._onScroll       = () => { if (this._open) this.position(); };
    this._onResize       = () => { if (this._open) this.position(); };
  }

  // ── Lit ReactiveController lifecycle ────────────────────────────

  hostConnected(): void {
    // Intentionally empty. Wiring is deferred to firstUpdated() via the host.
    // Wiring here would cause double-wiring (see FIX 1).
  }

  hostDisconnected(): void {
    this._teardown();
  }

  // ── Host integration API ─────────────────────────────────────────

  /**
   * Call from host's `firstUpdated()`.
   * Wires the trigger for the first time.
   */
  firstUpdated(triggerId: string | null, triggerSlot: HTMLSlotElement | null): void {
    this._triggerId       = triggerId;
    this._triggerSlot     = triggerSlot;
    this._firstRenderDone = false;  // updated() sets this true on its first call
    this._wire();
  }

  /**
   * Call from host's `updated(changed)`.
   * Skips the first call (handled by firstUpdated), then re-wires on
   * trigger-id changes.
   */
  updated(
    changed:     Map<PropertyKey, unknown>,
    triggerId:   string | null,
    triggerSlot: HTMLSlotElement | null,
  ): void {
    if (!this._firstRenderDone) {
      this._firstRenderDone = true;
      return;
    }
    if (changed.has('triggerId')) {
      this._triggerId   = triggerId;
      this._triggerSlot = triggerSlot;
      this._teardown();
      this._wire();
    }
  }

  /**
   * For components that render their own trigger button (e.g. uwc-dropdown).
   * Bypasses slot / trigger-id wiring entirely.
   */
  setTriggerElement(el: Element): void {
    this._triggerListenerCleanup?.();
    this._triggerListenerCleanup = null;
    this.triggerEl               = el;
    this._triggerListenerCleanup = this._onTriggerFound(el) ?? null;
  }

  // ── Wiring ───────────────────────────────────────────────────────

  private _wire(): void {
    if (this._triggerId)        this._wireExternal();
    else if (this._triggerSlot) this._wireInternal();
  }

  private _wireExternal(): void {
    const tryFind = (attempts = 0): void => {
      const el = deepQueryById(document, this._triggerId!);
      if (el) {
        this._callOnTriggerFound(el);
      } else if (attempts < 10) {
        setTimeout(() => tryFind(attempts + 1), 50);
      } else {
        console.warn(`[PlacementController] trigger #${this._triggerId} not found`);
      }
    };
    tryFind();
  }

  private _wireInternal(): void {
    const slot = this._triggerSlot!;
    const onSlotChange = (): void => {
      const [el] = slot.assignedElements({ flatten: true });
      if (el) this._callOnTriggerFound(el);
    };
    slot.addEventListener('slotchange', onSlotChange);
    this._cleanupFns.push(() => slot.removeEventListener('slotchange', onSlotChange));
    onSlotChange();  // handle already-assigned elements
  }

  private _callOnTriggerFound(el: Element): void {
    this._triggerListenerCleanup?.();
    this._triggerListenerCleanup = null;
    this.triggerEl               = el;
    this._triggerListenerCleanup = this._onTriggerFound(el) ?? null;
  }

  private _teardown(): void {
    this._cleanupFns.forEach(fn => fn());
    this._cleanupFns = [];
    this._triggerListenerCleanup?.();
    this._triggerListenerCleanup = null;
    this.triggerEl               = null;
    this.hide();
  }

  // ── Open / Hide ──────────────────────────────────────────────────

  show(): void {
    if (this._open) return;
    this._setOpen(true);

    this.host.updateComplete.then(() => {
      const panel = this._getPanelEl();
      if (!panel) return;
      panel.showPopover();

      // FIX 2: rAF ensures the browser has laid out the popover in the
      // top layer before we measure getBoundingClientRect().
      requestAnimationFrame(() => {
        this.position();
        this._startGlobalListeners();
        this._afterShow?.();
      });
    });
  }

  hide(): void {
    if (!this._open) return;
    this._setOpen(false);

    const panel = this._getPanelEl();
    if (panel) {
      try { panel.hidePopover(); } catch { /* already hidden or not supported */ }
      // FIX 3: Do NOT reset panel.style.top/left.
      // The overlay exit transition keeps the element in the top layer
      // while fading out; clearing position snaps it to 0,0 — visible flicker.
    }

    this._stopGlobalListeners();
    this._afterHide?.();
  }

  toggle(): void { this._open ? this.hide() : this.show(); }

  // ── Positioning ──────────────────────────────────────────────────

  position(): void {
    const panel = this._getPanelEl();
    if (!panel) return;

    const trigger = this.triggerEl
      ?? this._triggerSlot?.assignedElements({ flatten: true })[0]
      ?? null;
    if (!trigger) return;

    const placement = this.host.placement ?? 'bottom';
    const offset    = Number(this.host.offset ?? 8);
    const tRect     = trigger.getBoundingClientRect();
    const pRect     = panel.getBoundingClientRect();
    const raw       = computeCoords(tRect, pRect, placement, offset);
    const coords    = clampToViewport(raw, pRect);

    // FIX 4: Write directly as inline styles.
    // Normal inline style specificity [1,0,0,0] beats author stylesheet [0,0,1,0].
    // If stylesheet used !important, inline styles would lose → locked at 0,0.
    panel.style.top  = `${coords.top}px`;
    panel.style.left = `${coords.left}px`;

    this._afterPosition?.({ triggerRect: tRect, panelCoords: coords, panelEl: panel });
  }

  // ── Global listeners ─────────────────────────────────────────────

  private _startGlobalListeners(): void {
    window.addEventListener('scroll', this._onScroll, { passive: true, capture: true });
    window.addEventListener('resize', this._onResize, { passive: true });

    if (this._dismissOnEscape) {
      document.addEventListener('keydown', this._onEscapeKey);
    }

    if (this._dismissOnOutsideClick) {
      // FIX 5: setTimeout(0) — new task, past the full event-loop drain.
      // stopPropagation() on the trigger blocks bubbling only; the capture
      // phase fires BEFORE the target and would catch the opening click.
      this._outsideClickTimer = setTimeout(() => {
        document.addEventListener('click', this._onOutsideClick, { capture: true });
      }, 0);
    }
  }

  private _stopGlobalListeners(): void {
    window.removeEventListener('scroll', this._onScroll, { capture: true });
    window.removeEventListener('resize', this._onResize);
    document.removeEventListener('keydown', this._onEscapeKey);

    if (this._outsideClickTimer !== null) {
      clearTimeout(this._outsideClickTimer);
      this._outsideClickTimer = null;
    }
    document.removeEventListener('click', this._onOutsideClick, { capture: true });
  }

  private _handleOutsideClick(e: MouseEvent): void {
    const path  = e.composedPath();  // composedPath() crosses shadow boundaries
    const panel = this._getPanelEl();
    if (panel && !path.includes(panel) && !path.includes(this.triggerEl!)) {
      this.hide();
    }
  }

  private _handleEscapeKey(e: KeyboardEvent): void {
    if (e.key === 'Escape') {
      this.hide();
      (this.triggerEl as HTMLElement | null)?.focus();  // return focus (a11y)
    }
  }

  private _setOpen(val: boolean): void {
    this._open = val;
    this.host.requestUpdate();
  }
}
